import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Shield, FileText, RefreshCcw } from 'lucide-react';
import api from '@/api/api';
import ReactMarkdown from 'react-markdown';

const PolicyPage = () => {
  const { type } = useParams();
  const [policy, setPolicy] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchPolicy();
  }, [type]);

  const fetchPolicy = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/policies/public/main-site/${type}`);
      setPolicy(response.data);
    } catch (err) {
      setError('Policy not found');
    } finally {
      setLoading(false);
    }
  };

  const getIcon = () => {
    switch (type) {
      case 'privacy':
        return <Shield className="w-8 h-8" />;
      case 'terms':
        return <FileText className="w-8 h-8" />;
      case 'refund':
        return <RefreshCcw className="w-8 h-8" />;
      default:
        return <FileText className="w-8 h-8" />;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-800 mb-4">Policy Not Found</h1>
          <Link to="/" className="text-blue-600 hover:underline">Go back to home</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <Link to="/" className="inline-flex items-center text-blue-600 hover:text-blue-700 mb-4">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Home
          </Link>
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-blue-100 rounded-xl flex items-center justify-center text-blue-600">
              {getIcon()}
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">{policy?.title}</h1>
              <p className="text-sm text-gray-500">Last updated: {policy?.last_updated}</p>
            </div>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-white rounded-xl shadow-sm border p-8">
          <div className="prose prose-blue max-w-none">
            <ReactMarkdown>{policy?.content}</ReactMarkdown>
          </div>
        </div>

        {/* Company Info Card */}
        {policy?.company && (
          <div className="mt-8 bg-gradient-to-r from-blue-600 to-blue-700 rounded-xl p-6 text-white">
            <h3 className="font-semibold text-lg mb-4">Contact Information</h3>
            <div className="grid md:grid-cols-2 gap-4 text-blue-100">
              <div>
                <p className="font-medium text-white">{policy.company.name}</p>
                <p>{policy.company.address}</p>
              </div>
              <div>
                <p>Email: {policy.company.email}</p>
                <p>Phone: {policy.company.phone}</p>
              </div>
            </div>
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="bg-white border-t mt-12">
        <div className="max-w-4xl mx-auto px-4 py-6">
          <div className="flex flex-wrap justify-center gap-6 text-sm text-gray-600">
            <Link to="/policy/privacy" className="hover:text-blue-600">Privacy Policy</Link>
            <Link to="/policy/terms" className="hover:text-blue-600">Terms of Use</Link>
            <Link to="/policy/refund" className="hover:text-blue-600">Refund Policy</Link>
          </div>
          <p className="text-center text-gray-500 text-sm mt-4">
            © 2026 {policy?.company?.name}. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default PolicyPage;
